import ColorPicker from '@/components/commons/color-picker';
import { ElementProps } from '@/components/sections/builder/types';
import { ProductGridElementDataType } from '@/components/sections/builder/elements/body/collection-product-grid/type';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { Checkbox } from '@/components/ui/checkbox';

import { useBuilderStore } from '@/stores/builder';
import { ColorResult } from 'react-color';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ProductGridElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  return (
    <div>
      <div className='mb-2 text-xs'>Product per page</div>
      <InputSlider
        value={data.limit || 0}
        max={25}
        onChange={(val) =>
          builder.setElementData(element.id, {
            limit: val,
          })
        }
      />
      <div className='mb-2 mt-4 text-xs'>Number of columns on desktop</div>
      <InputSlider
        value={data.totalCol || 1}
        min={1}
        max={5}
        onChange={(val) =>
          builder.setElementData(element.id, {
            totalCol: val,
          })
        }
      />
      <div className='mb-2 mt-4 text-xs'>Number of columns on mobile</div>
      <InputSlider
        value={data.totalColMobile || 1}
        min={1}
        max={3}
        onChange={(val) =>
          builder.setElementData(element.id, {
            totalColMobile: val,
          })
        }
      />
      <div className='mb-2 mt-4'>Color scheme</div>
      <SchemeSetting
        value={data.colorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='enableQuickAddButton'
          aria-label='Select'
          className='mx-2'
          checked={data.enableQuickAddButton}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableQuickAddButton: !!checked,
            });
          }}
        />
        <label htmlFor='enableQuickAddButton'>Enable quick add button</label>
      </div>
      <h1 className='mb-2 mt-2 font-bold'>Product Card</h1>
      <div className='mb-4 mt-2'>Image ratio</div>
      <InputTab
        defaultValue={data.imageRatio}
        options={[
          { label: 'Adapt to image', value: 'adaptToImage' },
          { label: 'Portrait', value: 'portrait' },
          { label: 'Square', value: 'square' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            imageRatio: v,
          })
        }
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showImage'
          aria-label='Select'
          className='mx-2'
          checked={data.showImage}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showImage: !!checked,
            });
          }}
        />
        <label htmlFor='showImage'>Show image</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showSecondImageOnHover'
          aria-label='Select'
          className='mx-2'
          checked={data.showSecondImageOnHover}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showSecondImageOnHover: !!checked,
            });
          }}
        />
        <label htmlFor='showSecondImageOnHover'>Show second image on hover</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showShadow'
          aria-label='Select'
          className='mx-2'
          checked={data.showShadow}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showShadow: !!checked,
            });
          }}
        />
        <label htmlFor='showShadow'>Show shadow</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showCategory'
          aria-label='Select'
          className='mx-2'
          checked={data.showCategory}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showCategory: !!checked,
            });
          }}
        />
        <label htmlFor='showCategory'>Show Category</label>
      </div>
      <h1 className='mb-2 mt-4 font-bold'>Filtering and sorting</h1>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='enableFilter'
          aria-label='Select'
          className='mx-2'
          checked={data.enableFilter}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableFilter: !!checked,
            });
          }}
        />
        <label htmlFor='enableFilter'>Enable filtering</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showFilterCategory'
          aria-label='Select'
          className='mx-2'
          checked={data.showFilterCategory ?? true}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showFilterCategory: !!checked,
            });
          }}
        />
        <label htmlFor='showFilterCategory'>Show filter category</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showFilterAvailability'
          aria-label='Select'
          className='mx-2'
          checked={data.showFilterAvailability ?? true}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showFilterAvailability: !!checked,
            });
          }}
        />
        <label htmlFor='showFilterAvailability'>Show filter availability</label>
      </div>
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='showFilterPrice'
          aria-label='Select'
          className='mx-2'
          checked={data.showFilterPrice ?? true}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showFilterPrice: !!checked,
            });
          }}
        />
        <label htmlFor='showFilterPrice'>Show filter price</label>
      </div>
      <div className='mb-3 mt-4 text-xs'>Desktop filter layout</div>
      <InputTab
        defaultValue={data.filterLayout || 'horizontal'}
        options={[
          { label: 'Horizontal', value: 'horizontal' },
          { label: 'Vertical', value: 'vertical' },
          { label: 'Drawer', value: 'drawer' },
        ]}
        onChange={(v) =>
          builder.setElementData(element.id, {
            filterLayout: v,
          })
        }
      />
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='enableSort'
          aria-label='Select'
          className='mx-2'
          checked={data.enableSort}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableSort: !!checked,
            });
          }}
        />
        <label htmlFor='enableSort'>Enable sorting</label>
      </div>

      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
      {/* <BackgroundSetting
        value={data.background}
        onChange={(background) =>
          builder.setElementData(element.id, {
            background,
          })
        }
      /> */}
      <div className='mb-4 mt-4 font-bold'>Overlay</div>
      <ColorPicker
        color={data.overlay}
        inputColor={data.overlay}
        onChangeColorComplete={(color) => onChangeColor(color)}
        onChangeInputColor={(e) => builder.setElementData(element.id, { overlay: e.target.value })}
      />
    </div>
  );
}
